import React, { FC, useState } from 'react';
import { Icon } from 'semantic-ui-react';
import '../Dash.scss';

interface BudgetItem {
    name: string;
    startDate: string;
    endDate: string;
    children?: BudgetItem[];
}

const hardcodedData: BudgetItem[] = [
    {
        name: 'Parent 1',
        startDate: '01-01-2025',
        endDate: '12-31-2027',
        children: [
            {
                name: 'Child 1.1',
                startDate: '03-01-2025',
                endDate: '06-30-2025',
                children: [
                    { name: 'Grandchild 1.1.1', startDate: '03-15-2025', endDate: '05-15-2025' },
                    { name: 'Grandchild 1.1.2', startDate: '04-01-2025', endDate: '06-01-2025' },
                ],
            },
        ],
    },
    {
        name: 'Parent 2',
        startDate: '02-01-2024',
        endDate: '11-30-2026',
    },
    {
        name: 'Parent 3',
        startDate: '05-01-2023',
        endDate: '04-30-2025',
    },
];

const getYearRange = (data: BudgetItem[]): number[] => {
    const dates = data.flatMap(item => [
        new Date(item.startDate).getFullYear(),
        new Date(item.endDate).getFullYear(),
    ]);
    return [Math.min(...dates), Math.max(...dates)];
};

const formatCurrency = (value: number): string => {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
};

const DashBudget: FC = () => {
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [gridData, setGridData] = useState<Record<string, Record<number, number>>>({});
    const [editMode, setEditMode] = useState<Record<string, Record<number, boolean>>>({});
    const [tempValues, setTempValues] = useState<Record<string, Record<number, number>>>({}); 

    const yearRange = getYearRange(hardcodedData);

    const toggleExpand = (itemName: string) => {
        const newSet = new Set(expandedItems);
        if (newSet.has(itemName)) {
            newSet.delete(itemName);
        } else {
            newSet.add(itemName);
        }
        setExpandedItems(newSet);
    };

    const handleInputChange = (itemName: string, year: number, value: number) => {
        setGridData(prev => ({
            ...prev,
            [itemName]: {
                ...prev[itemName],
                [year]: value,
            },
        }));
    };

    const toggleEditMode = (itemName: string, year: number) => {
        const currentValue = gridData[itemName]?.[year] || 0;
        if (!editMode[itemName]?.[year]) {
            // Entering edit mode
            setTempValues(prev => ({
                ...prev,
                [itemName]: {
                    ...prev[itemName],
                    [year]: currentValue,
                },
            }));
        }
        setEditMode(prev => ({
            ...prev,
            [itemName]: {
                ...prev[itemName],
                [year]: !prev[itemName]?.[year],
            },
        }));
    };

    const cancelEdit = (itemName: string, year: number) => {
        // Revert to original value
        const originalValue = tempValues[itemName]?.[year] || 0;
        setGridData(prev => ({
            ...prev,
            [itemName]: {
                ...prev[itemName],
                [year]: originalValue,
            },
        }));
        toggleEditMode(itemName, year); // Exit edit mode
    };

    const calculateChildrenTotal = (item: BudgetItem, year: number): number => {
        if (!item.children) return 0;
        return item.children.reduce((sum, child) => {
            const childValue = gridData[child.name]?.[year] || 0;
            return sum + childValue + calculateChildrenTotal(child, year);
        }, 0);
    };

    const renderRow = (item: BudgetItem, level = 0): JSX.Element[] => {
        const rows: JSX.Element[] = [];
        rows.push(
            <div key={item.name} className={`budget-row`}>
                <div className={`budget-cell name-cell level-${level}`} style={{backgroundColor:'pink'}} >
                    {item.children && (
                        <Icon
                            name={expandedItems.has(item.name) ? 'chevron down' : 'chevron right'}
                            onClick={() => toggleExpand(item.name)}
                            style={{ cursor: 'pointer', marginRight: 8 }}
                        />
                    )}
                    {item.name}
                </div>
                {Array.from({ length: yearRange[1] - yearRange[0] + 1 }, (_, i) => {
                    const year = yearRange[0] + i;
                    const withinRange =
                        year >= new Date(item.startDate).getFullYear() &&
                        year <= new Date(item.endDate).getFullYear();
                    const isEditing = editMode[item.name]?.[year];
                    const value = gridData[item.name]?.[year] || 0;
                    const childrenTotal = calculateChildrenTotal(item, year);
                    const difference = value - childrenTotal;

                    return (
                        <div
                            key={`${item.name}-${year}`}
                            className={`budget-cell ${withinRange ? 'highlighted' : ''}`}
                        >
                            {!isEditing ? (
                                <span>
                                    {formatCurrency(value)}{' '}
                                    {childrenTotal !== 0 && (
                                        <span
                                            style={{
                                                color: difference < 0 ? 'red' : 'green',
                                            }}
                                        >
                                            ({formatCurrency(Math.abs(difference))})
                                        </span>
                                    )}
                                    <Icon
                                        name="pencil"
                                        className="icon"
                                        onClick={() => toggleEditMode(item.name, year)}
                                    />
                                </span>
                            ) : (
                                <span className="edit-mode">
                                    <input
                                        type="number"
                                        value={value}
                                        onChange={(e) =>
                                            handleInputChange(item.name, year, Number(e.target.value))
                                        }
                                        className="budget-input edit-input"
                                    />
                                    <Icon
                                        name="check"
                                        className="icon save-icon"
                                        onClick={() => toggleEditMode(item.name, year)}
                                    />
                                    <Icon
                                        name="close"
                                        className="icon cancel-icon"
                                        onClick={() => cancelEdit(item.name, year)}
                                    />
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        );

        if (item.children && expandedItems.has(item.name)) {
            item.children.forEach(child => rows.push(...renderRow(child, level + 1)));
        }

        return rows;
    };

    return (
        <div className="dash-budget">
            <div className="budget-year-axis">
                <div className="budget-cell name-cell">Name</div>
                {Array.from({ length: yearRange[1] - yearRange[0] + 1 }, (_, i) => (
                    <div key={i} className="budget-cell year-label">
                        {yearRange[0] + i}
                    </div>
                ))}
            </div>
            <div className="budget-content">
                {hardcodedData.map(parent => renderRow(parent))}
            </div>
        </div>
    );
};

export default DashBudget;